/**
 * Product Duo Section JavaScript
 * Handles variant selection and add to cart functionality
 */

class ProductDuo {
  constructor(container) {
    this.container = container;
    this.productForms = container.querySelectorAll('[data-product-form]');
    this.productFormInstances = [];
    this.quantityInputInstances = [];
    
    this.init();
  }

  init() {
    this.bindEvents();
    this.initializeProductForms();
    this.initializeQuantityInputs();
  }

  bindEvents() {
    // Handle variant selection
    this.container.addEventListener('change', (e) => {
      if (e.target.matches('[data-variant-option]')) {
        this.handleVariantChange(e.target);
      }
    });

    // Handle add to cart
    this.container.addEventListener('submit', (e) => {
      if (e.target.matches('[data-product-form]')) {
        e.preventDefault();
        this.handleAddToCart(e.target);
      }
    });
  }

  initializeProductForms() {
    this.productForms.forEach(form => {
      const productHandle = form.dataset.productHandle;
      const variantInput = form.querySelector('[data-variant-input]');
      
      if (variantInput) {
        // Store initial variant ID
        form.dataset.initialVariantId = variantInput.value;
      }
    });
  }

  initializeQuantityInputs() {
    // Initialize quantity inputs for each product duo item (like Featured Product)
    const productDuoItems = this.container.querySelectorAll('.product-duo-item');
    
    productDuoItems.forEach(item => {
      const quantityWrapper = item.querySelector('.quantity-input');
      if (!quantityWrapper) return;
      
      const quantityInput = quantityWrapper.querySelector('[data-quantity-input]');
      const addQuantity = quantityWrapper.querySelector('[data-add-quantity]');
      const subtractQuantity = quantityWrapper.querySelector('[data-subtract-quantity]');
      
      if (!quantityInput || !addQuantity || !subtractQuantity) return;
      
      const handleAddQuantity = () => {
        const currentValue = parseInt(quantityInput.value);
        const newValue = currentValue + 1;
        quantityInput.value = newValue;
        quantityInput.dispatchEvent(new Event('change'));
      };
      
      const handleSubtractQuantity = () => {
        const currentValue = parseInt(quantityInput.value);
        if (currentValue === 1) return;
        const newValue = currentValue - 1;
        quantityInput.value = newValue;
        quantityInput.dispatchEvent(new Event('change'));
      };
      
      // Add event listeners
      addQuantity.addEventListener('click', handleAddQuantity);
      subtractQuantity.addEventListener('click', handleSubtractQuantity);
      
      // Store cleanup function
      this.quantityInputInstances.push({
        unload: () => {
          addQuantity.removeEventListener('click', handleAddQuantity);
          subtractQuantity.removeEventListener('click', handleSubtractQuantity);
        }
      });
    });
  }

  handleVariantChange(selectElement) {
    const form = selectElement.closest('[data-product-form]');
    const productHandle = form.dataset.productHandle;
    const sectionId = form.dataset.sectionId;
    const variantInput = form.querySelector('[data-variant-input]');
    
    if (!variantInput || !sectionId) return;

    // Get selected option values
    const selectedOptions = {};
    const optionSelects = form.querySelectorAll('[data-variant-option]');
    
    optionSelects.forEach(select => {
      const optionName = select.name.match(/options\[(.*?)\]/)[1];
      selectedOptions[optionName] = select.value;
    });

    console.log('ProductDuo: Variant change debug', {
      productHandle,
      sectionId,
      selectedOptions,
      currentVariantId: variantInput.value
    });

    // Find matching variant using embedded product data
    // Determine which product this is based on the form's position in the section
    const productCard = form.closest('.product-duo__card');
    const productCards = form.closest('.product-duo__grid').querySelectorAll('.product-duo__card');
    const cardIndex = Array.from(productCards).indexOf(productCard) + 1;
    
    const productDataElement = document.getElementById(`ProductJson-${sectionId}-${cardIndex}`);
    if (!productDataElement) {
      console.error('ProductDuo: Product data element not found', `ProductJson-${sectionId}-${cardIndex}`);
      console.log('ProductDuo: Available ProductJson elements:', 
        Array.from(document.querySelectorAll('[id^="ProductJson-"]')).map(el => el.id)
      );
      return;
    }
    
    console.log('ProductDuo: Product data element found, content:', productDataElement.textContent);
    
    const product = JSON.parse(productDataElement.textContent);
    if (!product) {
      console.error('ProductDuo: Product data parsing failed');
      return;
    }

    console.log('ProductDuo: Product data loaded', {
      productTitle: product.title,
      variants: product.variants ? product.variants.map(v => ({ id: v.id, title: v.title, options: v.options })) : 'variants property missing',
      options: product.options,
      fullProduct: product
    });

    if (!product.variants || !Array.isArray(product.variants)) {
      console.error('ProductDuo: Product variants not found or not an array', product.variants);
      return;
    }

    const matchingVariant = product.variants.find(variant => {
      const matches = variant.options.every((option, index) => {
        const optionName = product.options[index];
        const isMatch = selectedOptions[optionName] === option;
        console.log(`ProductDuo: Checking variant ${variant.id} option ${index}:`, {
          optionName,
          selectedValue: selectedOptions[optionName],
          variantValue: option,
          matches: isMatch
        });
        return isMatch;
      });
      console.log(`ProductDuo: Variant ${variant.id} (${variant.title}) matches:`, matches);
      return matches;
    });

    if (matchingVariant) {
      console.log('ProductDuo: Found matching variant', {
        id: matchingVariant.id,
        title: matchingVariant.title,
        options: matchingVariant.options
      });
      
      variantInput.value = matchingVariant.id;
      
      // Update price display
      this.updatePriceDisplay(form, matchingVariant);
      
      // Update add to cart button state
      this.updateAddToCartButton(form, matchingVariant);
    } else {
      console.error('ProductDuo: No matching variant found for selected options', selectedOptions);
    }
  }

  updatePriceDisplay(form, variant) {
    const item = form.closest('.product-duo-item');
    if (!item) return;
    const priceContainer = item.querySelector('.product-duo-item__price');
    if (!priceContainer) return;

    const currentPrice = priceContainer.querySelector('.product-duo-item__price-current');
    let comparePrice = priceContainer.querySelector('.product-duo-item__price-compare');

    if (currentPrice) {
      currentPrice.textContent = this.formatMoney(variant.price);
    }

    const hasSale =
      variant.compare_at_price && variant.compare_at_price > variant.price;

    if (hasSale) {
      if (!comparePrice) {
        comparePrice = document.createElement('span');
        comparePrice.className = 'product-duo-item__price-compare';
        priceContainer.appendChild(comparePrice);
      }
      comparePrice.textContent = this.formatMoney(variant.compare_at_price);
      comparePrice.style.display = 'inline';
    } else if (comparePrice) {
      comparePrice.textContent = '';
      comparePrice.style.display = 'none';
    }
  }

  updateAddToCartButton(form, variant) {
    const addButton = form.querySelector('[data-add-to-cart]');
    const addButtonText = addButton.querySelector('[data-add-to-cart-text]');
    
    if (!addButton || !addButtonText) return;

    if (variant.available) {
      addButton.disabled = false;
      addButtonText.textContent = addButton.dataset.langAvailable || 'Add to cart';
    } else {
      addButton.disabled = true;
      addButtonText.textContent = addButton.dataset.langSoldOut || 'Sold out';
    }
  }

  async handleAddToCart(form) {
    const addButton = form.querySelector('[data-add-to-cart]');
    const variantInput = form.querySelector('[data-variant-input]');
    
    console.log('ProductDuo: handleAddToCart called', {
      form: form,
      addButton: addButton,
      variantId: variantInput?.value,
      cartExists: typeof cart !== 'undefined',
      cartAddItemExists: typeof cart !== 'undefined' && typeof cart.addItem === 'function'
    });
    
    // Show loading state
    this.setButtonLoading(addButton, true);
    
    try {
      // Use raw POST since theme cart object is not globally accessible
      console.log('ProductDuo: Using raw fetch to add to cart');
      const formData = new FormData(form);
      
      // Debug: Log all form data being sent
      console.log('ProductDuo: Form data being sent:');
      for (let [key, value] of formData.entries()) {
        console.log(`  ${key}: ${value}`);
      }
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        credentials: 'include',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });

      if (!response.ok) throw new Error('Failed to add to cart');

      const result = await response.json();
      console.log('ProductDuo: Add to cart result:', result);
      this.showAddToCartSuccess(addButton);

      // Trigger cart update events like the theme does
      document.dispatchEvent(new CustomEvent('apps:product-added-to-cart'));
      
      // Open the cart drawer after a delay
      setTimeout(() => {
        console.log('ProductDuo: About to call openCartDrawer');
        this.openCartDrawer();
      }, 300);
    } catch (error) {
      console.error('Add to cart error:', error);
      this.showAddToCartError(addButton);
    } finally {
      this.setButtonLoading(addButton, false);
    }
  }

  setButtonLoading(button, isLoading) {
    if (isLoading) {
      button.dataset.loading = 'true';
      button.disabled = true;
    } else {
      button.dataset.loading = 'false';
      button.disabled = false;
    }
  }

  showAddToCartSuccess(button) {
    const originalText = button.querySelector('span').textContent;
    const successText = button.dataset.langSuccess || 'Added!';
    
    button.querySelector('span').textContent = successText;
    button.classList.add('btn--success');
    
    setTimeout(() => {
      button.querySelector('span').textContent = originalText;
      button.classList.remove('btn--success');
    }, 2000);
  }

  showAddToCartError(button) {
    const originalText = button.querySelector('span').textContent;
    const errorText = button.dataset.langError || 'Error';
    
    button.querySelector('span').textContent = errorText;
    button.classList.add('btn--error');
    
    setTimeout(() => {
      button.querySelector('span').textContent = originalText;
      button.classList.remove('btn--error');
    }, 3000);
  }

  updateCartCount() {
    // Update cart count in header if it exists
    const cartCount = document.querySelector('.cart-count');
    if (cartCount) {
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          cartCount.textContent = cart.item_count;
        })
        .catch(error => console.error('Failed to update cart count:', error));
    }
  }

  openCartDrawer() {
    console.log('ProductDuo: Attempting to open cart drawer...');
    
    // Try multiple methods to open cart drawer
    const cartIcon = document.querySelector('[data-js-cart-icon]');
    const quickCart = document.querySelector('.quick-cart'); // Fixed selector
    const purchaseConfirmation = document.querySelector('[data-purchase-confirmation]');
    
    console.log('ProductDuo: Cart elements found:', {
      cartIcon: !!cartIcon,
      quickCart: !!quickCart,
      purchaseConfirmation: !!purchaseConfirmation,
      cartIconElement: cartIcon,
      quickCartElement: quickCart
    });
    
    // Method 1: Try to access theme's event system directly
    if (window.flu && window.flu.states && window.flu.states.emit) {
      console.log('ProductDuo: Using flu.states.emit');
      window.flu.states.emit('quick-cart:open');
      return;
    }
    
    // Method 1.5: Try to find the theme's r$1 function
    if (window.r$1) {
      console.log('ProductDuo: Using window.r$1');
      window.r$1('quick-cart:open');
      return;
    }
    
    // Method 1.6: Try to access quick-cart instance directly
    if (window.Shopify && window.Shopify.theme && window.Shopify.theme.sections) {
      const quickCartInstances = window.Shopify.theme.sections.instances.filter(instance => instance.type === 'quick-cart');
      if (quickCartInstances.length > 0 && quickCartInstances[0].openQuickCart) {
        console.log('ProductDuo: Using quick-cart instance directly');
        quickCartInstances[0].openQuickCart();
        return;
      }
    }
    
    // Method 2: Try to simulate cart icon click
    if (cartIcon) {
      console.log('ProductDuo: Simulating cart icon click');
      console.log('ProductDuo: Cart icon element:', cartIcon);
      console.log('ProductDuo: Cart icon href:', cartIcon.href);
      cartIcon.click();
      console.log('ProductDuo: Cart icon clicked');
      return;
    }
    
    // Method 3: Try purchase confirmation
    if (purchaseConfirmation) {
      console.log('ProductDuo: Triggering purchase confirmation');
      document.dispatchEvent(new CustomEvent('confirmation-popup:open'));
      return;
    }
    
    // Method 4: Try direct custom events
    if (quickCart) {
      console.log('ProductDuo: Triggering custom events');
      document.dispatchEvent(new CustomEvent('quick-cart:updated'));
      setTimeout(() => {
        document.dispatchEvent(new CustomEvent('quick-cart:open'));
      }, 300);
      return;
    }
    
    console.log('ProductDuo: No cart drawer method worked');
  }

  formatMoney(cents) {
    const currency =
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
    const locale = document.documentElement.lang || 'de-DE';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency
    }).format((cents || 0) / 100);
  }
}

// Initialize Product Duo sections
document.addEventListener('DOMContentLoaded', () => {
  const productDuoSections = document.querySelectorAll('[data-section-type="product-duo"]');
  productDuoSections.forEach(section => {
    new ProductDuo(section);
  });
});

// Cleanup method for ProductDuo instances
ProductDuo.prototype.destroy = function() {
  // Cleanup quantity input instances
  this.quantityInputInstances.forEach(instance => {
    if (instance && typeof instance.unload === 'function') {
      instance.unload();
    }
  });
  this.quantityInputInstances = [];
};

// Re-initialize on section reload (for theme editor)
document.addEventListener('shopify:section:load', (event) => {
  if (event.target.querySelector('[data-section-type="product-duo"]')) {
    const section = event.target.querySelector('[data-section-type="product-duo"]');
    new ProductDuo(section);
  }
});
